import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faExclamationCircle, faPersonChalkboard } from '@fortawesome/free-solid-svg-icons';
import { Player, TeamInLeague, GameState } from '../../../domain/entities';
import { AiService } from '../../../services/AiService';
import { RoleService } from '../../../services/RoleService';
import { ASSISTANT_MANAGER_LEVELS } from '../../../domain/constants';

interface ComprehensiveAssistantPanelProps {
    player: Player;
    team: TeamInLeague | null;
    tacticalPosition: string;
    isAiPunditEnabled: boolean;
    gameState: GameState;
    onCacheAiPlayerRoleAnalysis: (playerId: number, analysis: { roleName: string; roleDescription: string; }) => void;
    isPlayerTeamPlayer: boolean;
}

const generateAssessmentText = (player: Player, accuracy: number): string => {
    const assessmentPool = {
        high: [
            `${player.name} shows exceptional technical ability and game understanding for a ${player.position}.`,
            `Outstanding potential for development. This player could become a key asset with proper guidance.`,
            `Exceptional consistency and professionalism. Demonstrates strong mentality despite challenges.`,
            `Physical attributes are excellent - this player has the foundation to excel.`,
            `Demonstrates tactical intelligence above their current level.`,
        ],
        medium: [
            `${player.name} is a solid performer with room for improvement in key areas.`,
            `Has the fundamentals in place but needs development in specific tactical areas.`,
            `Shows promise, though may struggle against elite opposition.`,
            `Capable player with some notable strengths and weaknesses.`,
            `Potential is there, but development trajectory depends on consistent game time.`,
        ],
        low: [
            `${player.name} seems... functional. Hard to say more without closer inspection.`,
            `Player is difficult to fully assess from current data.`,
            `Some potential visible, though details are unclear.`,
            `Capabilities are somewhat unclear at this point.`,
            `Assessment limited with current information available.`,
        ],
        concerns: {
            fitnessIssue: `Currently struggling with fitness levels - needs rest and recovery management.`,
            moraleIssue: `Morale is a concern and may impact performance on the pitch.`,
            ageDecline: `Age is starting to be a factor - monitor physical decline closely.`,
            young: `Young player with significant room for growth and development.`,
        },
    };

    let assessment = '';

    if (accuracy >= 0.85) {
        // Legendary/Elite accuracy: detailed observations
        assessment = assessmentPool.high[Math.floor(Math.random() * assessmentPool.high.length)];

        if (player.fitness < 40) {
            assessment += ` ${assessmentPool.concerns.fitnessIssue}`;
        } else if (player.morale === 'Unhappy' || player.morale === 'Furious') {
            assessment += ` ${assessmentPool.concerns.moraleIssue}`;
        }

        if (player.age > 30) {
            assessment += ` ${assessmentPool.concerns.ageDecline}`;
        } else if (player.age < 25) {
            assessment += ` ${assessmentPool.concerns.young}`;
        }
    } else if (accuracy >= 0.7) {
        // Experienced/Seasoned: moderate observations
        assessment = assessmentPool.medium[Math.floor(Math.random() * assessmentPool.medium.length)];

        if (player.fitness < 40 || player.morale === 'Unhappy' || player.morale === 'Furious') {
            assessment += ` Something seems off with their current state.`;
        }
    } else {
        // Aspiring/lower: vague observations
        assessment = assessmentPool.low[Math.floor(Math.random() * assessmentPool.low.length)];
    }

    return assessment;
};

const generateAssistantReport = (player: Player, accuracy: number): string => {
    // Higher accuracy shows more detailed info, lower shows vague observations
    if (accuracy >= 0.85) {
        // Legendary/Elite: Full detailed report with role, mindset, potential, issues, morale
        const details: string[] = [];

        // Role analysis
        if (player.skill >= 70) {
            details.push(`Role: Key player, highly skilled (${player.skill}/100)`);
        } else if (player.skill >= 55) {
            details.push(`Role: Squad regular, solid performer (${player.skill}/100)`);
        } else {
            details.push(`Role: Depth option (${player.skill}/100)`);
        }

        // Potential
        if (player.potential > player.skill + 10) {
            details.push(`Potential: Significant room for growth (+${Math.round(player.potential - player.skill)})`);
        } else if (player.potential > player.skill) {
            details.push(`Potential: Some development possible`);
        } else {
            details.push(`Potential: Near peak ability`);
        }

        // Mindset/Form
        if (player.form >= 2) {
            details.push(`Mindset: Excellent form and confidence`);
        } else if (player.form >= 0) {
            details.push(`Mindset: Good shape`);
        } else if (player.form >= -2) {
            details.push(`Mindset: Struggling with form`);
        } else {
            details.push(`Mindset: Poor form, needs confidence boost`);
        }

        // Issues
        const issues: string[] = [];
        if (player.fitness < 20) {
            issues.push(`Critical fitness (${player.fitness}/100)`);
        } else if (player.fitness < 40) {
            issues.push(`Low fitness (${player.fitness}/100)`);
        }

        if (player.morale === 'Furious') {
            issues.push(`Furious - ${player.moraleReason || 'contract/performance'}`);
        } else if (player.morale === 'Unhappy') {
            issues.push(`Unhappy - ${player.moraleReason || 'unknown'}`);
        }

        if (player.contract.yearsRemaining <= 1) {
            issues.push(`Contract expiring S${player.contract.expirySeason}`);
        }

        if (player.age > 30) {
            issues.push(`Aging (${player.age}) - monitor decline`);
        }

        if (player.injury) {
            issues.push(`${player.injury.type} injury (${player.injury.duration}w)`);
        }

        if (issues.length > 0) {
            details.push(`Issues: ${issues.join('; ')}`);
        }

        return details.join('. ') + (issues.length === 0 ? ' No major concerns.' : '');
    } else if (accuracy >= 0.7) {
        // Experienced/Seasoned: General observations
        const details: string[] = [];

        // Basic role
        if (player.skill >= 65) {
            details.push(`Solid performer`);
        } else {
            details.push(`Squad player`);
        }

        // General potential
        if (player.potential > player.skill + 5) {
            details.push(`Still developing`);
        }

        // Form/mindset
        if (player.form >= 1) {
            details.push(`Playing well`);
        } else if (player.form < -1) {
            details.push(`Out of form`);
        }

        // Issues
        const issues: string[] = [];
        if (player.fitness < 30) {
            issues.push(`Low fitness`);
        }

        if (player.morale === 'Furious') {
            issues.push(`Very unhappy`);
        } else if (player.morale === 'Unhappy') {
            issues.push(`Morale issue`);
        }

        if (player.injury) {
            issues.push(`Injured`);
        }

        if (issues.length > 0) {
            details.push(`Concerns: ${issues.join(', ')}`);
        }

        return details.join('. ') + '.';
    } else {
        // Aspiring/Lower: Very vague observations
        const hasIssues = player.fitness < 40 || player.morale === 'Unhappy' || player.morale === 'Furious' || player.injury || player.contract.yearsRemaining <= 1;

        if (hasIssues) {
            return `Something appears off with this player, but hard to determine specifics.`;
        }
        return `Limited insight with current coaching level.`;
    }
};

export const ComprehensiveAssistantPanel: React.FC<ComprehensiveAssistantPanelProps> = ({
    player,
    team,
    tacticalPosition,
    isAiPunditEnabled,
    gameState,
    onCacheAiPlayerRoleAnalysis,
    isPlayerTeamPlayer,
}) => {
    const [roleAnalysis, setRoleAnalysis] = useState<{ roleName: string; roleDescription: string } | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Use the user's team's assistant manager level for assessment (not opposition team)
    const playerTeam = Object.values(gameState.leagues)
        .flatMap((league) => league.teams)
        .find((t) => t.name === gameState.playerTeamName);

    const assistantLevel = playerTeam?.assistantManager?.level || 1;
    const assistantInfo = ASSISTANT_MANAGER_LEVELS[assistantLevel - 1];
    const accuracy = assistantInfo?.accuracy || 0.6;

    // Determine display priority/color based on issues
    const hasLowFitness = player.fitness < 40;
    const hasLowMorale = player.morale === 'Unhappy' || player.morale === 'Furious';
    const priority = hasLowFitness ? 'fitness' : hasLowMorale ? 'morale' : null;

    // Generate assessment text and report based on accuracy
    const assessmentText = useMemo(() => {
        return generateAssessmentText(player, accuracy);
    }, [player, accuracy]);

    const assistantReport = useMemo(() => {
        return generateAssistantReport(player, accuracy);
    }, [player, accuracy]);

    useEffect(() => {
        let isCancelled = false;

        const loadAnalysis = async () => {
            if (isCancelled) return;
            setIsLoading(true);
            setError(null);

            try {
                // Load role analysis
                if (isCancelled) return;

                const isStale = !player.aiRoleAnalysis ||
                    player.aiRoleAnalysis.seasonGenerated < gameState.season ||
                    (player.aiRoleAnalysis.seasonGenerated === gameState.season && Math.floor(gameState.currentWeek) - player.aiRoleAnalysis.weekGenerated > 8);

                if (!isStale && player.aiRoleAnalysis) {
                    setRoleAnalysis(player.aiRoleAnalysis);
                } else {
                    if (isAiPunditEnabled && team) {
                        try {
                            const result = await AiService.generatePlayerRoleAnalysis(player, team, tacticalPosition);
                            if (!isCancelled) {
                                setRoleAnalysis(result);
                                onCacheAiPlayerRoleAnalysis(player.id, result);
                            }
                        } catch (e) {
                            console.error("AI Role Analysis failed, using fallback:", e);
                            const offlineResult = RoleService.getOfflinePlayerRole(player, tacticalPosition);
                            if (!isCancelled) setRoleAnalysis(offlineResult);
                        }
                    } else if (team) {
                        const offlineResult = RoleService.getOfflinePlayerRole(player, tacticalPosition);
                        if (!isCancelled) setRoleAnalysis(offlineResult);
                    }
                }
            } catch (e) {
                console.error("Error loading analysis:", e);
                setError("Failed to load analysis");
            } finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadAnalysis();

        return () => {
            isCancelled = true;
        };
    }, [player, team, tacticalPosition, isAiPunditEnabled, gameState, onCacheAiPlayerRoleAnalysis]);

    const borderColor = priority === 'fitness' ? 'var(--warning-color)' : priority === 'morale' ? 'var(--negative-color)' : undefined;

    return (
        <div className="panel" style={borderColor ? { borderColor } : undefined}>
            <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: borderColor }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={faPersonChalkboard} />
                    Assistant Manager
                </span>
                {priority && (
                    <FontAwesomeIcon
                        icon={priority === 'fitness' ? faExclamationTriangle : faExclamationCircle}
                        title={priority === 'fitness' ? 'Low fitness alert' : 'Morale issue'}
                        style={{
                            fontSize: '1.1em',
                            color: priority === 'fitness' ? 'var(--warning-color)' : 'var(--negative-color)'
                        }}
                    />
                )}
            </div>
            <div className="panel-content comprehensive-assistant">
                {isLoading ? (
                    <p style={{ opacity: 0.7, margin: 0, fontSize: '0.9em' }}>Analyzing player...</p>
                ) : (
                    <>
                        {/* Tactical Role Analysis */}
                        {roleAnalysis && (
                            <div>
                                <h4 style={{ color: 'var(--accent-text-color)', margin: '0 0 8px 0', fontSize: '0.95em' }}>
                                    <i className="fas fa-briefcase" style={{ marginRight: '8px' }}></i>
                                    Role: {roleAnalysis.roleName}
                                </h4>
                                <p style={{ margin: 0, fontSize: '0.9em', opacity: 0.85, lineHeight: 1.5 }}>
                                    {roleAnalysis.roleDescription}
                                </p>
                                {error && <p className="text-secondary" style={{ fontSize: '0.75em', marginTop: '4px', opacity: 0.7 }}>({error})</p>}
                            </div>
                        )}

                        {/* Player Assessment (accuracy-based text) */}
                        <div>
                            <h4 style={{ color: 'var(--accent-text-color)', margin: '0 0 8px 0', fontSize: '0.95em' }}>
                                <i className="fas fa-certificate" style={{ marginRight: '8px' }}></i>
                                Assessment
                            </h4>
                            <p style={{ margin: 0, fontSize: '0.9em', opacity: 0.85, lineHeight: 1.5 }}>
                                {assessmentText}
                            </p>
                        </div>

                        {/* Manager's Report (team players only) */}
                        {isPlayerTeamPlayer && (
                            <div style={priority ? { borderLeft: `3px solid ${borderColor}`, paddingLeft: '12px' } : undefined}>
                                <h4 style={{ color: borderColor || 'var(--accent-text-color)', margin: '0 0 8px 0', fontSize: '0.95em' }}>
                                    <i className="fas fa-file-alt" style={{ marginRight: '8px' }}></i>
                                    Report
                                </h4>
                                <p style={{ margin: 0, fontSize: '0.9em', opacity: 0.85, lineHeight: 1.5 }}>
                                    {assistantReport}
                                </p>
                            </div>
                        )}
                    </>
                )}
            </div>
            {/* Accuracy Disclaimer Footer */}
            <div style={{
                padding: '8px 12px',
                marginTop: '12px',
                borderTop: '1px solid var(--primary-color)',
                fontSize: '0.75em',
                opacity: 0.6,
                fontStyle: 'italic'
            }}>
                {accuracy < 0.8 ? (
                    <>Limited accuracy with current assistant manager level</>
                ) : (
                    <>Detailed analysis with expert assistant manager</>
                )}
            </div>
        </div>
    );
};
